import { mt, rd, RemoteData } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { Trash2 } from 'lucide-react';
import { useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import SystemUser from 'v1/components/shared/byModel/User/SystemUser/SystemUser';
import { Button } from 'v5/design-system/Button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from 'v5/design-system/Form';
import { TextArea } from 'v5/design-system/Input';
import { Skeleton } from 'v5/design-system/Skeleton';
import { useFormSubmitHotkeys } from 'v5/platform/react/useFormSubmit';
import { cn } from 'v5/platform/dom/cn';
import { WithServices } from 'v5/platform/ts/services';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { EntityListHeader } from 'v5/patterns/entity-list/EntityListHeader';
import { PageInsetListLayout } from 'v5/patterns/page-layout/PageInsetListLayout';

export type NotesPanelNote = {
  id: number;
  note: string;
  created_at: string;
  /** Account / user id for {@link SystemUser} resolution */
  user_id?: string | number;
  user?: Record<string, unknown>;
  archived?: boolean;
};

export type NotesPanelCopy = {
  title: string;
  fieldLabel: string;
  placeholder: string;
  submit: string;
  emptyTitle: string;
  emptyDescription: string;
  loadError: string;
  unknownUser: string;
  deleteAria: string;
  createdToast: string;
  createErrorToast: string;
  deletedToast: string;
  deleteErrorToast: string;
};

export interface NotesPanelProps extends WithServices<[WithToastService]> {
  notes: RemoteData<NotesPanelNote[]>;
  onCreate: (note: string) => Promise<unknown>;
  onDelete: (noteId: number) => Promise<unknown>;
  disabled: boolean;
  copy: NotesPanelCopy;
}

type NoteFormValues = { note: string };

function formatNoteDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** Notes attached to an entity: an add form above the list, newest first. On its host's background. */
export function NotesPanel({
  services,
  notes: notesRd,
  onCreate,
  onDelete,
  disabled,
  copy
}: NotesPanelProps) {
  const { toastService } = services;

  const activeNotes = rd.map(notesRd, notes =>
    notes
      .filter(n => !n.archived)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
  );
  const count = rd.tryGet(activeNotes)?.length ?? 0;

  const form = useForm<NoteFormValues>({
    defaultValues: { note: '' }
  });
  const noteBodyRef = useRef<HTMLTextAreaElement | null>(null);
  const noteDraft = useWatch({ control: form.control, name: 'note' });

  const createNoteMutation = promiseState.useMutation(onCreate);
  const deleteNoteMutation = promiseState.useMutation(onDelete);

  const submitNote = form.handleSubmit(async values => {
    const text = values.note.trim();
    if (!text) {
      return;
    }
    try {
      await createNoteMutation.track(text);
      form.reset();
      toastService.open({ type: 'success', message: copy.createdToast });
      window.setTimeout(() => {
        noteBodyRef.current?.focus({ preventScroll: true });
      }, 0);
    } catch {
      toastService.open({ type: 'error', message: copy.createErrorToast });
    }
  });

  const handleDeleteNote = async (noteId: number) => {
    try {
      await deleteNoteMutation.track(noteId);
      toastService.open({ type: 'success', message: copy.deletedToast });
    } catch {
      toastService.open({ type: 'error', message: copy.deleteErrorToast });
    }
  };

  const createInProgress = mt.isInProgress(createNoteMutation.state);
  const deleteInProgress = mt.isInProgress(deleteNoteMutation.state);

  const hotkeysRef = useFormSubmitHotkeys(submitNote);

  return (
    <div className="tailwind-root contents">
      <PageInsetListLayout
        className="bg-transparent"
        header={<EntityListHeader title={copy.title} count={rd.of(count)} />}
        actions={null}
        contentClassName="pt-0"
        content={
          <div className="space-y-6">
            {!disabled ? (
              <Form {...form}>
                <form
                  onSubmit={submitNote}
                  className="space-y-3"
                  ref={hotkeysRef}
                >
                  <FormField
                    control={form.control}
                    name="note"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{copy.fieldLabel}</FormLabel>
                        <FormControl>
                          <TextArea
                            {...field}
                            ref={el => {
                              field.ref(el);
                              noteBodyRef.current = el;
                            }}
                            rows={3}
                            placeholder={copy.placeholder}
                            disabled={createInProgress}
                            className="min-h-24"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end">
                    <AsyncButton
                      type="submit"
                      variant="primary"
                      size="sm"
                      mutation={createNoteMutation.state}
                      disabled={!String(noteDraft ?? '').trim()}
                    >
                      {copy.submit}
                    </AsyncButton>
                  </div>
                </form>
              </Form>
            ) : null}
            <div
              className={cn(!disabled && 'border-t border-border-default pt-6')}
            >
              {rd
                .journey(activeNotes)
                .wait(
                  <div className="space-y-3">
                    <Skeleton className="h-24 w-full rounded-md" />
                    <Skeleton className="h-24 w-full rounded-md" />
                  </div>
                )
                .catch(() => (
                  <p className="text-sm text-fg-danger">{copy.loadError}</p>
                ))
                .map(notes => {
                  if (notes.length === 0) {
                    return (
                      <div className="rounded-md border border-border-default bg-bg-subtle px-6 py-10 text-center">
                        <p className="text-base font-medium text-fg-default">
                          {copy.emptyTitle}
                        </p>
                        <p className="mt-2 text-sm text-fg-muted">
                          {copy.emptyDescription}
                        </p>
                      </div>
                    );
                  }
                  return (
                    <ul className="space-y-3">
                      {notes.map(note => (
                        <li
                          key={note.id}
                          className="rounded-md border border-border-default bg-bg-default p-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1 space-y-2">
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-fg-muted">
                                {note.user_id != null &&
                                String(note.user_id) !== '' ? (
                                  <SystemUser
                                    // @ts-ignore
                                    id={note.user_id}
                                    user={note.user}
                                    includeImage
                                    className="min-w-0 text-fg-default [&_.SystemUser-details]:font-medium"
                                  />
                                ) : (
                                  <span className="font-medium text-fg-default">
                                    {copy.unknownUser}
                                  </span>
                                )}
                                <span className="shrink-0 tabular-nums">
                                  {formatNoteDate(note.created_at)}
                                </span>
                              </div>
                              <p className="whitespace-pre-wrap wrap-break-word text-base text-fg-default">
                                {note.note}
                              </p>
                            </div>
                            {!disabled ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="shrink-0 text-fg-muted hover:text-fg-danger"
                                aria-label={copy.deleteAria}
                                disabled={deleteInProgress}
                                onClick={() => void handleDeleteNote(note.id)}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  );
                })}
            </div>
          </div>
        }
      />
    </div>
  );
}
